package com.exura.features.plandefinition.domain.builder.series

import com.exura.features.plandefinition.domain.builder.BuilderField
import com.exura.features.plandefinition.domain.model.element.PersistentElement
import com.exura.features.plandefinition.domain.model.series.HourOfDay
import com.exura.features.plandefinition.domain.model.series.SeriesSpawnType
import com.exura.features.plandefinition.domain.model.series.SimplePersistentSeries

class SimplePersistentSeriesBuilderNew : BaseAnchoredSeriesBuilder<PersistentElement, SimplePersistentSeries>(
    SeriesSpawnType.PERSISTENT
) {

    private val durationDays = BuilderField<Int?>()
    private val durationInMinutes = BuilderField<Int?>()
    private val startHourOfDay = BuilderField<HourOfDay?>()
    private val icon = BuilderField<String?>()

    fun durationDays(): Int? = durationDays.getValue()
    fun durationInMinutes(): Int? = durationInMinutes.getValue()
    fun startHourOfDay(): HourOfDay? = startHourOfDay.getValue()
    fun icon(): String? = icon.getValue()
    fun setDurationDays(newValue: Int?) = apply {
        val res = durationDays.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)

        // Reset minutes when setting days
        if (newValue != null) {
            durationInMinutes.setValue(null)
        }
    }
    fun setDurationInMinutes(newValue: Int?) = apply {
        val res = durationInMinutes.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)

        // Reset days when setting minutes
        if (newValue != null) {
            durationDays.setValue(null)
        }
    }
    fun setStartHourOfDay(newValue: HourOfDay?) = apply {
        val res = startHourOfDay.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    fun setStartHourOfDay(hour: Int, minute: Int) = apply {
        setStartHourOfDay(HourOfDay(hour, minute))
    }

    fun setIcon(newValue: String?) = apply {
        val res = icon.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    override fun markSaved() = apply {
        durationDays.markSaved()
        durationInMinutes.markSaved()
        startHourOfDay.markSaved()
        icon.markSaved()

        super.markSaved()
    }

    override fun validate(): List<String> {
        val fields = mapOf(
            "durationDays" to durationDays,
            "durationInMinutes" to durationInMinutes,
            "startHourOfDay" to startHourOfDay,
            "icon" to icon,
        )
        val messages = validateFields(fields).toMutableList()
        // Ensure one and only one duration specification is provided
        val hasDurationDays = durationDays.getValue() != null
        val hasDurationInMinutes = durationInMinutes.getValue() != null

        if (hasDurationDays == hasDurationInMinutes) {
            messages.add("Either durationDays or durationInMinutes must be specified, but not both")
        }
        return messages + super.validate()
    }

    override fun build(): SimplePersistentSeries {
        return SimplePersistentSeries(
            firebaseDocId = firebaseDocId(),
            firebaseDocPrefix = null, // Will be set when saved to Firebase
            title = name() ?: "",
            description = description() ?: "",
            relativeDay = relativeDay() ?: 0,
            durationDays = durationDays(),
            durationInMinutes = durationInMinutes(),
            startHourOfDay = startHourOfDay(),
            icon = icon(),
            imageUrl = imageUrl()
        )
    }
}

fun SimplePersistentSeries.toBuilder(): SimplePersistentSeriesBuilderNew {
    val series = this
    return SimplePersistentSeriesBuilderNew().apply {
        setFirebaseDocId(series.firebaseDocId)
        setName(series.title)
        setDescription(series.description)
        setImageUrl(series.imageUrl)
        setRelativeDay(series.relativeDay)
        setDurationDays(series.durationDays)
        setDurationInMinutes(series.durationInMinutes)
        setStartHourOfDay(series.startHourOfDay)
        setIcon(series.icon)
        markSaved()
    }
}
